'use client'

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  startTransition,
} from 'react'
import type { EnrichedMediaResult, MediaType } from './enrich'
// The analysis result shapes are `capture/`'s vocabulary, and this hook is `enrichment/`'s when
// step 5 moves it — an `enrichment` → `capture` edge, against the direction `capture` calls
// `enrichment` in. It reads an analysis as input rather than producing one; whether step 5 keeps
// the edge or narrows the input to a name and its hints is that step's call.
import type {
  MovieAnalysis,
  BookAnalysis,
  ArtistAnalysis,
  LabelAnalysis,
  ReleaseAnalysis,
} from '@/capture'

type MediaAnalysis =
  | MovieAnalysis
  | BookAnalysis
  | ArtistAnalysis
  | LabelAnalysis
  | ReleaseAnalysis

/** AI fallback info to pass to enrich-media */
interface AIFallbackInfo {
  summary?: string
  genres?: string[]
}

/** Configuration constants */
const ENRICHMENT_TIMEOUT_MS = 15000 // 15 second timeout
const MAX_RETRY_COUNT = 3

/** User-friendly error messages */
const ERROR_MESSAGES = {
  timeout: 'Request timed out. Check your connection and try again.',
  network: 'Network error. Please check your internet connection.',
  server: 'Server error. Please try again later.',
  unknown: 'Something went wrong. Please try again.',
} as const

interface UseMediaEnrichmentOptions {
  /** The detected media type */
  type: MediaType | null
  /** The AI analysis result */
  analysis: MediaAnalysis | null
  /** Whether enrichment is enabled */
  enabled?: boolean
  /** Timeout in milliseconds (default: 15000) */
  timeout?: number
}

interface UseMediaEnrichmentResult {
  /** Enriched data from external API */
  enrichedData: EnrichedMediaResult | null
  /** Whether enrichment is in progress */
  isEnriching: boolean
  /** Error message if enrichment failed */
  error: string | null
  /** Reset the enrichment state */
  reset: () => void
  /** Retry the enrichment (clears cache and re-fetches) */
  retry: () => void
  /** Manually update the enriched data (e.g., when user selects an alternative) */
  setEnrichedData: (data: EnrichedMediaResult | null) => void
}

/**
 * Get user-friendly error message from error
 */
function getErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (err.name === 'AbortError') {
      return ERROR_MESSAGES.timeout
    }
    if (err.message.includes('fetch') || err.message.includes('network')) {
      return ERROR_MESSAGES.network
    }
    if (err.message.includes('500') || err.message.includes('server')) {
      return ERROR_MESSAGES.server
    }
    // Return the error message if it's user-friendly (short)
    if (err.message.length < 100) {
      return err.message
    }
  }
  return ERROR_MESSAGES.unknown
}

/**
 * Hook to enrich media analysis with external API data (TMDB, OpenLibrary, Discogs)
 */
export function useMediaEnrichment({
  type,
  analysis,
  enabled = true,
  timeout = ENRICHMENT_TIMEOUT_MS,
}: UseMediaEnrichmentOptions): UseMediaEnrichmentResult {
  const [enrichedData, setEnrichedData] = useState<EnrichedMediaResult | null>(
    null,
  )
  const [isEnriching, setIsEnriching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const abortControllerRef = useRef<AbortController | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastEnrichedKeyRef = useRef<string>('')
  const [retryCount, setRetryCount] = useState(0)
  const retryCountRef = useRef(0) // Track actual retries for limit

  const reset = useCallback(() => {
    setEnrichedData(null)
    setError(null)
    setIsEnriching(false)
    lastEnrichedKeyRef.current = ''
    retryCountRef.current = 0
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const retry = useCallback(() => {
    // Check retry limit
    if (retryCountRef.current >= MAX_RETRY_COUNT) {
      setError(
        `Maximum retries (${MAX_RETRY_COUNT}) reached. Please try again later.`,
      )
      return
    }

    retryCountRef.current += 1

    // Clear the last enriched key to force re-fetch
    lastEnrichedKeyRef.current = ''
    setError(null)
    setEnrichedData(null)
    // Increment retry count to trigger useEffect
    setRetryCount((c) => c + 1)
  }, [])

  useEffect(() => {
    // Skip if no type or analysis
    if (!type || !analysis || !enabled) {
      return
    }

    // Extract name/title from analysis
    let name: string | undefined
    let extraData: Record<string, unknown> = {}

    switch (type) {
      case 'movie':
        name = (analysis as MovieAnalysis).title
        extraData = { year: (analysis as MovieAnalysis).year }
        break
      case 'book': {
        const bookAnalysis = analysis as BookAnalysis
        name = bookAnalysis.title
        // Build AI fallback from analysis (summary for books)
        const bookFallback: AIFallbackInfo = {
          summary: bookAnalysis.summary,
        }
        extraData = {
          author: bookAnalysis.author,
          aiFallback: bookFallback,
        }
        break
      }
      case 'artist': {
        const artistAnalysis = analysis as ArtistAnalysis
        name = artistAnalysis.name
        // Build AI fallback from analysis (summary + genres for artists)
        const artistFallback: AIFallbackInfo = {
          summary: artistAnalysis.summary,
          genres: artistAnalysis.genres,
        }
        extraData = {
          explicitType: artistAnalysis.explicitType,
          aiFallback: artistFallback,
        }
        break
      }
      case 'label': {
        const labelAnalysis = analysis as LabelAnalysis
        name = labelAnalysis.name
        // Build AI fallback from analysis (summary + genres for labels)
        const labelFallback: AIFallbackInfo = {
          summary: labelAnalysis.summary,
          genres: labelAnalysis.genres,
        }
        extraData = {
          explicitType: labelAnalysis.explicitType,
          aiFallback: labelFallback,
        }
        break
      }
      case 'release': {
        const releaseAnalysis = analysis as ReleaseAnalysis
        name = releaseAnalysis.title
        extraData = {
          artistName: releaseAnalysis.artistName,
          explicitType: releaseAnalysis.explicitType,
        }
        break
      }
    }

    if (!name) {
      return
    }

    // Create a key to prevent duplicate enrichment
    const enrichKey = `${type}:${name}:${JSON.stringify(extraData)}`
    if (lastEnrichedKeyRef.current === enrichKey) {
      return
    }

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    // Use startTransition to avoid synchronous setState warning in effect
    startTransition(() => {
      setIsEnriching(true)
      setError(null)
    })

    // Set up timeout
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, timeout)
    timeoutRef.current = timeoutId

    // Call enrich-media API
    fetch('/api/enrich-media', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, name, ...extraData }),
      signal: controller.signal,
    })
      .then(async (response) => {
        clearTimeout(timeoutId)

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          const statusText =
            response.status >= 500 ? 'Server error' : 'Request failed'
          throw new Error(
            errorData.error || `${statusText} (${response.status})`,
          )
        }
        return response.json()
      })
      .then((data: EnrichedMediaResult) => {
        lastEnrichedKeyRef.current = enrichKey
        setEnrichedData(data)
        // Reset retry count on success
        retryCountRef.current = 0
      })
      .catch((err) => {
        clearTimeout(timeoutId)

        // Don't show error for intentional aborts (e.g., unmount or new request)
        if (err instanceof Error && err.name === 'AbortError') {
          // Check if this was a timeout or intentional abort
          if (
            !abortControllerRef.current ||
            abortControllerRef.current === controller
          ) {
            setError(ERROR_MESSAGES.timeout)
          }
          return
        }

        console.error('Media enrichment error:', err)
        setError(getErrorMessage(err))
      })
      .finally(() => {
        setIsEnriching(false)
      })

    return () => {
      clearTimeout(timeoutId)
      controller.abort()
    }
  }, [type, analysis, enabled, retryCount, timeout])

  // Reset cache when analysis changes to allow fresh enrichment for new entries
  useEffect(() => {
    return () => {
      lastEnrichedKeyRef.current = ''
    }
  }, [type, analysis])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    enrichedData,
    isEnriching,
    error,
    reset,
    retry,
    setEnrichedData,
  }
}
